// @flow
import React, { Component } from 'react';
import styled from 'styled-components';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { FormGroup as _FormGroup, type InputProps } from './Input';

const COUNTRY_CODE_MAP = {
    bhs: 'bs',
    bda: 'bm',
    bmu: 'bm',
    brb: 'bb',
    bvi: 'vg',
    vgb: 'vg',
    cym: 'ky',
    tci: 'tc',
    tca: 'tc',
    usa: 'us',
};

export const mapJurisdictionToPhoneCountry = (threeCharValue = '') => {
    const defaultCountry = 'bm';
    return COUNTRY_CODE_MAP[threeCharValue.toLowerCase()] || defaultCountry;
};

type Props = {
    // https://github.com/bl00mber/react-phone-input-2#options
    ...InputProps,
    type: 'tel',
    country: ?string,
    value: ?string,
    validate: () => void,
    onChange: () => void,
    onBlur: () => void,
    autoComplete: ?string,
};

export const FormGroup = styled(_FormGroup)`
    .react-tel-input.form-control {
        width: 100% !important;
        input[type='tel'],
        input[type='tel']:focus {
            border: none !important;
            background: white !important;
            padding: 1px 3px;
            border-color: ${props => props.theme.color.borderPrimary} !important;
        }
        input[type='tel']:is-invalid {
            border-color: ${props => props.theme.color.borderDanger} !important;
        }
    }
    .react-tel-input .flag-dropdown.invalid-number {
        border-color: ${props => props.theme.color.borderDanger} !important;
    }
`;

export class PhoneNumberInputOnly extends Component {
    static defaultProps = {
        country: 'bm',
        preferredCountries: ['bs', 'bm', 'bb', 'vg', 'ky', 'tc', 'us', 'gb'],
        autoComplete: 'off',
    };

    constructor(props) {
        super(props);
        this.deformattedValue = this.deformattedValue.bind(this);
    }

    deformattedValue = formattedValue => {
        // when clearing the value, it still sends in the '+' by itself, which we don't want
        if (formattedValue === '+') {
            return '';
        }

        // the formattedValue contains the plus sign and, depending on mask, spaces, parens and/or dashes.
        // we need the '+' for an international number validation, but not the non-numbers.
        return formattedValue
            .replace(/\s/g, '')
            .replace(/-/g, '')
            .replace(/\(/g, '')
            .replace(/\)/, '');
    };

    render() {
        const {
            autoComplete,
            errorText,
            name,
            onChange,
            onBlur,
            preferredCountries,
            validate,
            ...fieldProps
        } = this.props;
        const phoneValidate = (value = '', country = {}) => {
            const { iso2 } = country;
            validate(value, iso2);
            if (errorText) {
                return false;
            } else {
                return true;
            }
        };

        return (
            <PhoneInput
                inputProps={{
                    name: name,
                    id: `id_${name}`,
                    className: `form-control ${errorText ? 'is-invalid' : ''}`,
                    autoComplete: autoComplete,
                }}
                preferredCountries={preferredCountries}
                enableTerritories={true}
                enableSearch={true}
                isValid={(value, country) => phoneValidate(value, country)}
                {...fieldProps}
                onChange={(value, country, e, formattedValue) => {
                    onChange(this.deformattedValue(formattedValue));
                }}
                onBlur={onBlur}
                defaultMask='...-....'
                masks={{
                    us: '... ...-....',
                    pa: '........',
                }}
                enableAreaCodeStretch
            />
        );
    }
}

export class PhoneNumberInput extends Component<Props> {
    static defaultProps = {
        label: 'Phone Number',
    };

    render() {
        const {
            autoComplete,
            errorText,
            helpText,
            label,
            name,
            onChange,
            onBlur,
            preferredCountries,
            validate,
            ...fieldProps
        } = this.props;

        return (
            <FormGroup>
                {label && <label htmlFor={`id_${name}`}>{label}</label>}
                <PhoneNumberInputOnly
                    autoComplete={autoComplete}
                    name={name}
                    onChange={onChange}
                    onBlur={onBlur}
                    preferredCountries={preferredCountries}
                    validate={validate}
                    {...fieldProps}
                />
                {errorText && (
                    <small className='form-text text-danger'>{errorText}</small>
                )}
                {helpText && <small className='form-text text-muted'>{helpText}</small>}
            </FormGroup>
        );
    }
}

export default PhoneNumberInput;
